import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard,
  Users,
  Ticket,
  CreditCard,
  Activity,
  FileText,
  Smartphone,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  Settings,
  Search,
  Wifi,
  Shield,
  AlertTriangle,
  // DollarSign, // Cash payment removed
} from 'lucide-react';

const Layout = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, current: location.pathname === '/admin', color: 'from-blue-500 to-blue-600' },
    { name: 'Plans', href: '/admin/plans', icon: CreditCard, current: location.pathname === '/admin/plans', color: 'from-purple-500 to-purple-600' },
    { name: 'WireGuard Peers', href: '/admin/users', icon: Users, current: location.pathname === '/admin/users', color: 'from-green-500 to-green-600' },
    { name: 'Vouchers', href: '/admin/vouchers', icon: Ticket, current: location.pathname === '/admin/vouchers', color: 'from-orange-500 to-orange-600' },
    { name: 'Sessions', href: '/admin/sessions', icon: Activity, current: location.pathname === '/admin/sessions', color: 'from-cyan-500 to-cyan-600' },
    // { name: 'Cash Orders', href: '/admin/cash-orders', icon: DollarSign, current: location.pathname === '/admin/cash-orders', color: 'from-yellow-500 to-yellow-600' }, // Cash payment removed
    { name: 'Mobile App', href: '/admin/mobile', icon: Smartphone, current: location.pathname === '/admin/mobile', color: 'from-pink-500 to-pink-600' },
    { name: 'Devices', href: '/admin/devices', icon: ShieldCheck, current: location.pathname === '/admin/devices', color: 'from-indigo-500 to-indigo-600' },
    { name: 'Audit Logs', href: '/admin/logs', icon: FileText, current: location.pathname === '/admin/logs', color: 'from-gray-500 to-gray-600' },
  ];

  const activeItem = navigation.find((item) => item.current) || navigation[0];

  const NavItems = () => (
    <nav className="space-y-0.5">
      {navigation.map((item) => (
        <a
          key={item.name}
          href={item.href}
          className={`group flex items-center px-2 py-2 text-sm rounded-md transition-colors ${
            item.current 
              ? 'bg-gray-900 text-white' 
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
          }`}
          onClick={() => setSidebarOpen(false)}
        >
          <item.icon className={`h-4 w-4 mr-3 flex-shrink-0 ${item.current ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'}`} />
          <span className="font-medium">
            {item.name}
          </span>
        </a>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            sidebarOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Mobile Sidebar */}
        <aside
          className={`absolute inset-y-0 left-0 w-64 bg-white shadow-xl transform transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Sidebar Header */}
            <div className="px-4 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="/logo.png" alt="Sateva" className="h-6 w-6 object-contain" />
                  <h2 className="text-sm font-semibold text-gray-900">Sateva Control</h2>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 rounded hover:bg-gray-100 transition"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="flex-1 overflow-y-auto p-3">
              <NavItems />
            </div>

            {/* Mobile Sidebar Footer */}
            <div className="p-3 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56">
        <aside className="flex flex-col w-full bg-white border-r border-gray-200">
          {/* Logo Section */}
          <div className="px-4 py-5 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Sateva" className="h-6 w-6 object-contain" />
              <h2 className="text-sm font-semibold text-gray-900">
                Sateva Control
              </h2>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto p-3">
            <NavItems />
          </div>

          {/* User Profile & Actions */}
          <div className="p-3 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gray-900 rounded">
                  <Users className="h-3 w-3 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {user?.username || 'Admin'}
                  </p>
                  <p className="text-xs text-gray-500">Administrator</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </aside>
      </div>

      {/* Main Content Area */}
      <div className="lg:pl-56">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded hover:bg-gray-100 transition"
              >
                <Menu className="h-5 w-5 text-gray-600" />
              </button>
              
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="Sateva" className="h-6 w-6" />
                <span className="text-sm font-semibold text-gray-900">
                  Sateva
                </span>
              </div>

              <div className="w-9 h-9"></div>
            </div>
          </div>
        </header>


        {/* Page Content */}
        <main>
          <Outlet />
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Logout</h3>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to sign out? You'll need to log in again to access the admin panel.
              </p>
              
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition sm:order-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition flex items-center justify-center gap-2 sm:order-2"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;